import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock3, ExternalLink, MapPin, Navigation } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiError } from "../lib/api";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";

const DAY_MS = 24 * 60 * 60 * 1000;

function toDate(dateString) {
  const [year, month, day] = String(dateString || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function isThisWeekend(date) {
  if (!date) return false;
  const today = startOfToday();
  const diffDays = Math.floor((date - today) / DAY_MS);
  if (diffDays < 0 || diffDays > 6) return false;
  const day = date.getDay();
  return day === 0 || day === 6;
}

function statusMeta(event) {
  if (event.status === "Canceled") {
    return { label: "Canceled", className: "bg-rose-700/20 text-rose-300 border-rose-600/40" };
  }

  const eventDate = toDate(event.event_date);
  const today = startOfToday();
  if (!eventDate) {
    return { label: "Upcoming", className: "bg-[#D8A34B]/20 text-[#D8A34B] border-[#D8A34B]/40" };
  }
  if (eventDate.getTime() < today.getTime()) {
    return { label: "Past Event", className: "bg-zinc-700/40 text-[#B8B7B2] border-stone-500/30" };
  }
  if (eventDate.getTime() === today.getTime()) {
    return { label: "Today", className: "bg-emerald-700/25 text-emerald-300 border-emerald-600/40" };
  }
  if (isThisWeekend(eventDate)) {
    return { label: "This Weekend", className: "bg-[#26C6D5]/20 text-[#26C6D5] border-[#26C6D5]/40" };
  }
  return { label: "Upcoming", className: "bg-[#D8A34B]/20 text-[#D8A34B] border-[#D8A34B]/40" };
}

function formatDate(dateString) {
  const dt = toDate(dateString);
  if (!dt) return dateString;
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatShortDate(dateString) {
  const dt = toDate(dateString);
  if (!dt) return dateString;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Readable multi-day label: consecutive days collapse to a range
// ("Oct 10–12, 2026"), non-consecutive days list out ("Oct 10, Oct 12 & Oct 17, 2026").
function formatEventDates(event) {
  const keys = [...new Set([event.event_date, ...(event.event_dates || [])].filter(Boolean))].sort();
  if (keys.length === 0) return "";
  if (keys.length === 1) return formatDate(keys[0]);
  const days = keys.map((key) => ({ key, date: toDate(key) })).filter((entry) => entry.date);
  const year = days[days.length - 1].date.getFullYear();
  const consecutive = days.every((entry, index) => index === 0 || (entry.date - days[index - 1].date) === DAY_MS);
  if (consecutive) {
    return `${formatShortDate(days[0].key)}\u2013${days[days.length - 1].date.getDate()}, ${year}`;
  }
  const labels = days.map((entry) => formatShortDate(entry.key));
  return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}, ${year}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, offset) {
  return new Date(date.getFullYear(), date.getMonth() + offset, 1);
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getMonthGrid(monthDate) {
  const start = startOfMonth(monthDate);
  const firstWeekday = start.getDay();
  const gridStart = new Date(start);
  gridStart.setDate(start.getDate() - firstWeekday);

  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const cell = new Date(gridStart);
    cell.setDate(gridStart.getDate() + i);
    cells.push(cell);
  }
  return cells;
}

function formatMonthLabel(date) {
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function formatClock(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return time;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function eventDestination(event) {
  if (event.full_address) return event.full_address;
  const parts = [event.location_name, event.city, [event.state, event.zip_code].filter(Boolean).join(" ")].filter(Boolean);
  return parts.join(", ");
}

function mapUrl(event) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventDestination(event))}`;
}

function calendarUrl(event) {
  const start = toDate(event.event_date);
  if (!start) return "#";

  const [startHour, startMinute] = String(event.start_time || "09:00").split(":").map(Number);
  const [endHour, endMinute] = String(event.end_time || "17:00").split(":").map(Number);

  const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate(), startHour || 9, startMinute || 0);
  const endDate = new Date(start.getFullYear(), start.getMonth(), start.getDate(), endHour || 17, endMinute || 0);

  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    event.event_name
  )}&dates=${fmt(startDate)}/${fmt(endDate)}&location=${encodeURIComponent(eventDestination(event))}&details=${encodeURIComponent(
    event.long_description || event.short_description || ""
  )}`;
}

function EventCard({ event }) {
  const status = statusMeta(event);
  const detailsLink = event.event_website_link || event.facebook_event_link || "";
  const imageUrl = event.event_image_filename ? `${API}/events/assets/${event.event_image_filename}` : "";
  const dateLabel = formatEventDates(event);

  return (
    <article className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 overflow-hidden">
      {imageUrl ? (
        <div className="aspect-[16/8] overflow-hidden border-b border-stone-100/10">
          <img src={imageUrl} alt={event.event_name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : null}
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-2xl text-stone-100">{event.event_name}</h3>
            <p className="text-stone-300 mt-2">{event.short_description}</p>
          </div>
          <span className={`shrink-0 px-3 py-1 rounded-full text-xs border ${status.className}`}>{status.label}</span>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <p className="flex items-center gap-2 text-stone-300"><CalendarDays className="w-4 h-4 text-[#D8A34B]" /> {dateLabel}</p>
          <p className="flex items-center gap-2 text-stone-300"><Clock3 className="w-4 h-4 text-[#D8A34B]" /> {formatClock(event.start_time)} - {formatClock(event.end_time)}</p>
          <p className="flex items-center gap-2 text-stone-300 md:col-span-2"><MapPin className="w-4 h-4 text-[#D8A34B]" /> {event.location_name}</p>
          <p className="text-stone-400 md:col-span-2">{event.full_address}</p>
          {event.booth_number ? <p className="text-stone-300 md:col-span-2">Booth/Table: {event.booth_number}</p> : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          {event.event_website_link ? (
            <a href={event.event_website_link} target="_blank" rel="noreferrer" className="text-[#D8A34B] hover:text-[#F5F0E6] inline-flex items-center gap-1">
              Event Website <ExternalLink className="w-3 h-3" />
            </a>
          ) : null}
          {event.facebook_event_link ? (
            <a href={event.facebook_event_link} target="_blank" rel="noreferrer" className="text-[#D8A34B] hover:text-[#F5F0E6] inline-flex items-center gap-1">
              Facebook Event <ExternalLink className="w-3 h-3" />
            </a>
          ) : null}
          <a href={mapUrl(event)} target="_blank" rel="noreferrer" className="text-[#D8A34B] hover:text-[#F5F0E6] inline-flex items-center gap-1">
            Google Maps <Navigation className="w-3 h-3" />
          </a>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a href={mapUrl(event)} target="_blank" rel="noreferrer" className="w-full">
            <Button className="w-full h-11 bg-[#D8A34B] hover:bg-[#c6903b] text-[#15191C]">Get Directions</Button>
          </a>
          {detailsLink ? (
            <a href={detailsLink} target="_blank" rel="noreferrer" className="w-full">
              <Button variant="outline" className="w-full h-11 bg-transparent border-stone-100/20 text-stone-100 hover:bg-stone-100/10">Event Details</Button>
            </a>
          ) : (
            <Button disabled variant="outline" className="w-full h-11 bg-transparent border-stone-100/20 text-stone-500">Event Details</Button>
          )}
          <a href={calendarUrl(event)} target="_blank" rel="noreferrer" className="w-full">
            <Button variant="outline" className="w-full h-11 bg-transparent border-stone-100/20 text-stone-100 hover:bg-stone-100/10">Add to Calendar</Button>
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMonth, setViewMonth] = useState(startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/events")
      .then((res) => {
        if (!active) return;
        const sorted = [...(res.data || [])].sort((a, b) => String(a.event_date).localeCompare(String(b.event_date)));
        setEvents(sorted);
        setError(null);
      })
      .catch((err) => {
        if (!active) return;
        const msg = formatApiError(err);
        setError(msg);
        toast.error(msg);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const { featuredEvent, upcomingEvents, pastEvents, monthEventsByDay, selectedDayEvents } = useMemo(() => {
    const today = startOfToday();
    const upcoming = [];
    const past = [];

    for (const event of events) {
      const dt = toDate(event.event_date);
      if (dt && dt.getTime() < today.getTime() && event.status !== "Canceled") {
        past.push(event);
      } else if (dt && dt.getTime() < today.getTime() && event.status === "Canceled") {
        past.push(event);
      } else {
        upcoming.push(event);
      }
    }

    const featured = upcoming.find((event) => Boolean(event.featured_event));
    const monthEvents = (featured ? upcoming.filter((event) => event.id !== featured.id) : upcoming).sort((a, b) => String(a.event_date).localeCompare(String(b.event_date)));

    const monthMap = new Map();
    upcoming.forEach((event) => {
      const keys = [...new Set([event.event_date, ...(event.event_dates || [])].filter(Boolean))];
      keys.forEach((key) => {
        if (!monthMap.has(key)) monthMap.set(key, []);
        if (!monthMap.get(key).some((existing) => existing.id === event.id)) monthMap.get(key).push(event);
      });
    });

    const allSelectedDayEvents = selectedDay ? (monthMap.get(selectedDay) || []) : [];

    return {
      featuredEvent: featured || null,
      upcomingEvents: monthEvents,
      pastEvents: past.sort((a, b) => String(b.event_date).localeCompare(String(a.event_date))),
      monthEventsByDay: monthMap,
      selectedDayEvents: allSelectedDayEvents,
    };
  }, [events, selectedDay]);

  const monthGrid = useMemo(() => getMonthGrid(viewMonth), [viewMonth]);
  const monthDays = monthGrid.filter((day, index) => index < 42);

  return (
    <div className="min-h-screen">
      <Seo
        title="Events & Markets"
        description="Find Beyond Board Woodworking at local craft shows, vendor markets, and pop-ups. See upcoming events, shop finished pieces, and talk custom work in person."
        path="/events"
      />
      <section className="relative overflow-hidden border-b border-stone-100/10 wood-grain" data-testid="events-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a120d] via-zinc-950 to-[#100d0b] opacity-95" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-4">Beyond Board Events</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F0E6]">Come See Us In Person</h1>
          <p className="mt-6 max-w-4xl text-lg text-stone-300 leading-relaxed">
            Find Beyond Board Woodwork at local craft shows, vendor markets, pop-ups, and community events. Stop by to shop finished pieces, see our work up close, and talk with us about custom orders.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#upcoming-events">
              <Button className="events-natural-button h-12 px-6">View Upcoming Events</Button>
            </a>
            <Link to="/quote">
              <Button variant="outline" className="events-natural-button h-12 px-6">Request a Custom Order</Button>
            </Link>
          </div>
        </div>
      </section>

      {featuredEvent ? (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="events-featured">
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <h2 className="font-display text-3xl text-stone-100">Featured Upcoming Event</h2>
            <p className="text-sm text-stone-400">Handpicked highlight from our schedule</p>
          </div>
          <div className="rounded-3xl border border-[#D8A34B]/40 bg-gradient-to-br from-[#2b1b11] to-zinc-900 overflow-hidden">
            <EventCard event={featuredEvent} />
          </div>
        </section>
      ) : null}

      <section id="upcoming-events" className="max-w-7xl mx-auto px-6 lg:px-10 py-6" data-testid="events-upcoming">
        <h2 className="font-display text-3xl text-stone-100">Upcoming Events</h2>
        <p className="text-stone-400 mt-2">Meet us in person at markets, fairs, pop-ups, and local events.</p>

        {loading ? <p className="text-stone-400 mt-6">Loading events...</p> : null}

        {!loading && error ? (
          <div className="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6 text-rose-200">
            We could not load the event schedule right now. Please try again in a moment.
          </div>
        ) : null}

        {!loading && !error && upcomingEvents.length === 0 && !featuredEvent ? (
          <div className="mt-6 rounded-2xl border border-[#D8A34B]/25 bg-[#D8A34B]/5 p-6 text-[#F5F0E6]">
            <p className="font-display text-2xl text-[#F5F0E6]">No upcoming events are currently listed.</p>
            <p className="mt-2 text-[#B8B7B2]">Check back soon for new craft shows, pop-ups, and local market dates.</p>
          </div>
        ) : null}

        {!loading && !error ? (
          <div className="events-calendar mt-8 rounded-3xl p-4 sm:p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-[#D8A34B]">Calendar</p>
                <h3 className="font-display text-3xl text-stone-100 mt-2">{formatMonthLabel(viewMonth)}</h3>
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" className="events-natural-button h-10 px-3" onClick={() => setViewMonth(addMonths(viewMonth, -1))}>Prev</Button>
                <Button type="button" variant="outline" className="events-natural-button h-10 px-3" onClick={() => setViewMonth(new Date())}>Today</Button>
                <Button type="button" variant="outline" className="events-natural-button h-10 px-3" onClick={() => setViewMonth(addMonths(viewMonth, 1))}>Next</Button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2 text-center text-xs uppercase tracking-[0.2em] text-stone-400">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="py-2">{day}</div>
              ))}
            </div>

            <div className="mt-2 grid grid-cols-7 gap-2">
              {monthDays.map((day) => {
                const dayKey = day.toISOString().slice(0, 10);
                const eventsOnDay = monthEventsByDay.get(dayKey) || [];
                const isCurrentMonth = day.getMonth() === viewMonth.getMonth();
                const isSelected = selectedDay === dayKey;
                const isToday = isSameDay(day, new Date());

                return (
                  <button
                    key={dayKey}
                    type="button"
                    onClick={() => setSelectedDay(dayKey)}
                    className={[
                      'events-calendar-day relative min-h-[120px] rounded-2xl border p-2 text-left transition-colors overflow-hidden',
                      isCurrentMonth ? 'events-calendar-day-current' : 'events-calendar-day-outside',
                      isSelected ? 'events-calendar-day-selected' : '',
                      isToday && !isSelected ? 'events-calendar-day-today' : '',
                    ].join(' ')}
                  >
                    {eventsOnDay.length > 0 ? (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 bottom-1 mx-auto h-28 w-28 select-none bg-contain bg-center bg-no-repeat"
                        style={{ backgroundImage: "url('/branding/bbw-stamp.png')" }}
                      />
                    ) : null}
                    <div className="absolute left-1 top-1 z-10 flex items-center">
                      <span className={['inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium', isToday ? 'bg-[#D8A34B] text-[#15191C]' : ''].join(' ')}>
                        {day.getDate()}
                      </span>
                    </div>
                    {eventsOnDay.length > 0 ? (
                      <div className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center">
                        <span className="bbw-burned-text text-xs font-semibold tracking-wide">
                          {eventsOnDay.length} event{eventsOnDay.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 rounded-2xl border border-stone-100/10 bg-zinc-950/60 p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h4 className="font-display text-2xl text-stone-100">
                  {selectedDay ? `Events on ${new Date(`${selectedDay}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Selected day'}
                </h4>
                {!selectedDay ? <span className="text-sm text-stone-400">Choose a highlighted day to view details.</span> : null}
              </div>

              {selectedDay && selectedDayEvents.length === 0 ? (
                <p className="mt-4 text-stone-400">No events scheduled on this day.</p>
              ) : null}

              {selectedDayEvents.length > 0 ? (
                <div className="mt-4 space-y-4">
                  {selectedDayEvents.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="events-past">
        <h2 className="font-display text-3xl text-stone-100">Past Events</h2>
        <p className="text-stone-400 mt-2">A quick look at where Beyond Board Woodwork has been.</p>

        {pastEvents.length === 0 ? (
          <p className="mt-5 text-stone-400">No past events listed yet.</p>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {pastEvents.map((event) => {
              const imageUrl = event.event_image_filename ? `${API}/events/assets/${event.event_image_filename}` : "";
              return (
                <article key={event.id} className="rounded-2xl border border-stone-100/10 bg-zinc-900/50 overflow-hidden">
                  {imageUrl ? (
                    <div className="aspect-[16/7] overflow-hidden border-b border-stone-100/10">
                      <img src={imageUrl} alt={event.event_name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  ) : null}
                  <div className="p-5">
                    <h3 className="font-display text-2xl text-stone-100">{event.event_name}</h3>
                    <p className="text-stone-300 mt-1">{formatEventDates(event)}</p>
                    <p className="text-stone-400 mt-1">{event.location_name}</p>
                    <p className="text-stone-400 mt-2">{event.short_description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="py-14 border-y border-stone-100/10 bg-zinc-900/60 wood-grain" data-testid="events-hosting-callout">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl text-stone-100">Hosting a vendor event?</h2>
          <p className="text-stone-300 mt-3 max-w-4xl">
            If you are organizing a craft show, vendor market, community event, or pop-up opportunity, contact us about becoming a vendor.
          </p>
          <Link to="/contact">
            <Button className="events-natural-button mt-6 h-12 px-6">Contact Beyond Board</Button>
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="events-custom-cta">
        <div className="rounded-3xl border border-[#D8A34B]/35 bg-gradient-to-br from-[#2a1c12] to-zinc-900 p-8 lg:p-10">
          <h2 className="font-display text-4xl text-stone-100">Want to talk custom work in person?</h2>
          <p className="text-stone-300 mt-4 max-w-3xl leading-relaxed">
            Events are a great place to see our pieces up close, ask questions, and talk through custom signs, gifts, and decor ideas.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/quote">
              <Button className="events-natural-button h-12 px-6">View Custom Orders</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="events-natural-button h-12 px-6">Contact Us</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
